import {
  EARTH_AXIAL_TILT_DEGREES,
  LUNAR_ORBIT_INCLINATION_DEGREES,
  MOON_ORBIT_RADIUS,
  REPRESENTATIVE_NODE_LONGITUDE_DEGREES,
  SYNODIC_MONTH_DAYS,
} from "./constants";
import { PHASE_NAMES } from "./types";
import type { SimulationSnapshot, Vector3 } from "./types";
import {
  angleBetweenDegrees,
  clamp,
  degreesToRadians,
  dot,
  normalize,
  radiansToDegrees,
  rotateAroundAxis,
  scale,
} from "./vector";

const ECLIPTIC_NORMAL: Vector3 = [0, 1, 0];

function getNodeAxis(): Vector3 {
  const longitude = degreesToRadians(REPRESENTATIVE_NODE_LONGITUDE_DEGREES);
  return [Math.cos(longitude), 0, Math.sin(longitude)];
}

export function wrapLunarDay(day: number): number {
  return ((day % SYNODIC_MONTH_DAYS) + SYNODIC_MONTH_DAYS) % SYNODIC_MONTH_DAYS;
}

export function phaseIndexToDay(index: number): number {
  return wrapLunarDay((index / PHASE_NAMES.length) * SYNODIC_MONTH_DAYS);
}

export function getMoonPosition(day: number): Vector3 {
  const angle = (wrapLunarDay(day) / SYNODIC_MONTH_DAYS) * Math.PI * 2;
  const basePosition: Vector3 = [
    -MOON_ORBIT_RADIUS * Math.cos(angle),
    0,
    MOON_ORBIT_RADIUS * Math.sin(angle),
  ];
  return rotateAroundAxis(
    basePosition,
    getNodeAxis(),
    degreesToRadians(LUNAR_ORBIT_INCLINATION_DEGREES),
  );
}

export function getEarthAxis(): Vector3 {
  return rotateAroundAxis(
    ECLIPTIC_NORMAL,
    [1, 0, 0],
    degreesToRadians(EARTH_AXIAL_TILT_DEGREES),
  );
}

export function getOrbitalNormal(): Vector3 {
  return rotateAroundAxis(
    ECLIPTIC_NORMAL,
    getNodeAxis(),
    degreesToRadians(LUNAR_ORBIT_INCLINATION_DEGREES),
  );
}

export interface EclipseNodes {
  readonly ascending: Vector3;
  readonly descending: Vector3;
}

// Where the tilted lunar orbit crosses the ecliptic. Eclipses are only
// possible when a new or full moon happens near one of these crossings.
export function getEclipseNodes(): EclipseNodes {
  const axis = getNodeAxis();
  return {
    ascending: scale(axis, MOON_ORBIT_RADIUS),
    descending: scale(axis, -MOON_ORBIT_RADIUS),
  };
}

export function getSimulationSnapshot(day: number): SimulationSnapshot {
  const lunarDay = wrapLunarDay(day);
  const orbitalAngleRadians = (lunarDay / SYNODIC_MONTH_DAYS) * Math.PI * 2;
  const moonPosition = getMoonPosition(lunarDay);
  const moonToSun: Vector3 = [-1, 0, 0];
  const moonToEarth = normalize(scale(moonPosition, -1));
  const cosine = clamp(dot(moonToSun, moonToEarth), -1, 1);
  const phaseAngleRadians = Math.acos(cosine);
  const illuminatedFraction = (1 + cosine) / 2;
  const phaseIndex =
    Math.floor((orbitalAngleRadians + Math.PI / 8) / (Math.PI / 4)) % 8;
  const phaseName = PHASE_NAMES[phaseIndex];

  if (phaseName === undefined) {
    throw new RangeError(`Invalid phase index: ${phaseIndex}`);
  }

  return {
    lunarDay,
    orbitalAngleRadians,
    orbitalAngleDegrees: radiansToDegrees(orbitalAngleRadians),
    moonPosition,
    moonToSun,
    moonToEarth,
    orbitalNormal: getOrbitalNormal(),
    earthAxis: getEarthAxis(),
    illuminatedFraction,
    phaseAngleRadians,
    phaseIndex,
    phaseName,
    isWaxing: lunarDay > 0 && lunarDay < SYNODIC_MONTH_DAYS / 2,
  };
}

export { angleBetweenDegrees };
